// Metrics registry for the demo Lens. Passed through Metrics.getLensMetrics
// via `registryOverride` so the demo reports work without a Lens subscription;
// parseRawMetricData in src/api/lens/Metrics.js expects this array shape.
//
// Keys match the production registry (lens.metrics_registry_v2). It's a
// curated set of standard metrics only: nothing account-specific such as
// custom conversions, app events or Shops-only metrics.

const metric = (key, name, type, parentGroup, description, extra = {}) => ({
  key,
  name,
  type,
  metric_direction: 'positive',
  parent_group: parentGroup,
  description,
  status: 1,
  ...extra
})
const costMetric = (key, name, parentGroup, description) => metric(key, name, 'currency', parentGroup, description, { metric_direction: 'negative' })
const score = (key, name, description) => metric(key, name, 'percentile', 'foreplay_metrics', description, { child_group: 'calculated_scores' })

const DEMO_METRICS_REGISTRY = [
  // Foreplay Scores: 0-100 percentile of the underlying rate across the ads in view
  score('hook_score', 'Hook Score', 'How well the first seconds stop the scroll, relative to the other ads in view.'),
  score('watch_score', 'Watch Score', 'How long people keep watching, relative to the other ads in view.'),
  score('click_score', 'Click Score', 'How often people click through, relative to the other ads in view.'),
  score('convert_score', 'Convert Score', 'How efficiently the ad converts, relative to the other ads in view.'),
  score('viral_engagement_score', 'Viral Engagement Score', 'How much the ad gets liked, commented and shared, relative to the other ads in view.'),

  // Performance
  metric('spend', 'Spend', 'currency', 'performance', 'Total amount spent on the ad.'),
  metric('percent_of_spend', '% of Spend', 'percent', 'performance', "Share of the report's total spend."),
  metric('impressions', 'Impressions', 'number', 'performance', 'Number of times the ad was shown.'),
  metric('reach', 'Reach', 'number', 'performance', 'Number of people who saw the ad at least once.'),
  metric('frequency', 'Frequency', 'number', 'performance', 'Average number of times each person saw the ad.', { metric_direction: 'negative' }),
  costMetric('cpm', 'CPM', 'performance', 'Cost per 1,000 impressions.'),
  metric('roas', 'ROAS', 'number', 'performance', 'Conversion value divided by spend.'),
  costMetric('cpa', 'CPA', 'performance', 'Spend divided by conversions.'),
  metric('aov', 'AOV', 'currency', 'performance', 'Average conversion value per conversion.'),
  metric('revenue_per_mille', 'Revenue per 1,000 Impressions', 'currency', 'performance', 'Conversion value per 1,000 impressions.'),
  metric('purchases_per_mille', 'Purchases per 1,000 Impressions', 'number', 'performance', 'Conversions per 1,000 impressions.'),

  // Engagement
  metric('clicks', 'Clicks (All)', 'number', 'engagement', 'All clicks on the ad.'),
  metric('ctr', 'CTR (All)', 'percent', 'engagement', 'Clicks divided by impressions.'),
  costMetric('cpc', 'CPC (All)', 'engagement', 'Spend divided by clicks.'),
  metric('link_clicks', 'Link Clicks', 'number', 'engagement', 'Clicks on links that lead to the destination.'),
  metric('link_ctr', 'Link CTR', 'percent', 'engagement', 'Link clicks divided by impressions.'),
  costMetric('link_cpc', 'Link CPC', 'engagement', 'Spend divided by link clicks.'),
  metric('outbound_clicks', 'Outbound Clicks', 'number', 'engagement', 'Clicks that take people off Meta.'),
  metric('outbound_ctr', 'Outbound CTR', 'percent', 'engagement', 'Outbound clicks divided by impressions.'),
  costMetric('outbound_cpc', 'Outbound CPC', 'engagement', 'Spend divided by outbound clicks.'),
  metric('engagements', 'Engagements', 'number', 'engagement', 'Likes, comments, shares and other interactions.'),
  metric('engagement_rate', 'Engagement Rate', 'percent', 'engagement', 'Engagements divided by impressions.'),
  metric('like_rate', 'Like Rate', 'percent', 'engagement', 'Likes divided by impressions.'),
  metric('share_ratio', 'Share Rate', 'percent', 'engagement', 'Shares divided by impressions.'),
  metric('see_more_rate', 'See More Rate', 'percent', 'engagement', 'How often people expand the primary text.'),

  // Media
  metric('thumbstop_ratio', 'Thumbstop Ratio', 'percent', 'media', '3-second video plays divided by impressions.'),
  metric('hook_rate', 'Hook Rate', 'percent', 'media', 'Share of impressions that watched at least 3 seconds.'),
  metric('hold_rate', 'Hold Rate', 'percent', 'media', 'ThruPlays divided by impressions.'),
  metric('first_frame_retention', 'First Frame Retention', 'percent', 'media', 'Share of viewers still watching after the first frame.'),
  metric('three_second_video_view', '3-Second Video Plays', 'number', 'media', 'Video plays of at least 3 seconds.'),
  metric('thruplays', 'ThruPlays', 'number', 'media', 'Video plays to completion, or at least 15 seconds.'),
  costMetric('cost_per_thruplay', 'Cost per ThruPlay', 'media', 'Spend divided by ThruPlays.'),
  metric('video_p25_watch_rate', 'Video 25% Watch Rate', 'percent', 'media', 'Share of plays that reached 25% of the video.'),
  metric('video_p50_watch_rate', 'Video 50% Watch Rate', 'percent', 'media', 'Share of plays that reached 50% of the video.'),
  metric('video_p75_watch_rate', 'Video 75% Watch Rate', 'percent', 'media', 'Share of plays that reached 75% of the video.'),
  metric('video_p100_watch_rate', 'Video 100% Watch Rate', 'percent', 'media', 'Share of plays that reached the end of the video.'),

  // Conversions
  metric('total_conversions', 'Purchases', 'number', 'conversions', 'Purchases attributed to the ad.'),
  metric('conversion_value', 'Purchase Value', 'currency', 'conversions', 'Value of purchases attributed to the ad.'),
  metric('percent_of_purchase_value', '% of Purchase Value', 'percent', 'conversions', "Share of the report's total purchase value."),
  metric('conversion_rate', 'Conversion Rate', 'percent', 'conversions', 'Purchases divided by link clicks.')
]

export default DEMO_METRICS_REGISTRY

export const DEMO_METRIC_KEYS = DEMO_METRICS_REGISTRY.map((entry) => entry.key)

// Counts that add up across ads and scale with the date range; every other
// metric is derived from these (see lensDemoMetrics.js)
export const DEMO_SUM_METRIC_KEYS = [
  'spend', 'impressions', 'reach', 'clicks', 'link_clicks', 'outbound_clicks', 'engagements',
  'three_second_video_view', 'thruplays', 'total_conversions', 'conversion_value'
]

// Percentile scores: ranked per result set, never averaged or deviated
export const DEMO_SCORE_METRIC_KEYS = DEMO_METRICS_REGISTRY
  .filter((entry) => entry.parent_group === 'foreplay_metrics')
  .map((entry) => entry.key)

// Shares of the report total, filled in once the totals are known
export const DEMO_RELATIVE_METRIC_KEYS = ['percent_of_spend', 'percent_of_purchase_value']
